/**
 * Test IDs for the MMS Sales page and all its sub-components.
 *
 * Naming convention:  {page}-{section}-{element}-{qualifier?}
 * All IDs are kebab-case and stable — do NOT use display labels or i18n strings.
 *
 * Usage:
 *   import { MMS_SALES_TEST_IDS as TID } from "@/constant/testIds/mmsSales.testIds";
 *   <button data-testid={TID.EXPORT_BTN}>Export</button>
 *   <div data-testid={TID.TABLE_ROW(orderId)}>...</div>
 */

export const MMS_SALES_TEST_IDS = {
  // ── Page shell ────────────────────────────────────────────────────────────
  PAGE: 'sales-page',

  // ── Toolbar ───────────────────────────────────────────────────────────────
  SEARCH_INPUT: 'sales-search-order-id',
  EXPORT_BTN: 'sales-export-btn',
  DATE_FILTER: 'sales-date-filter',

  // ── Filters (CommonDropdownSelector) ──────────────────────────────────────
  /** Root wrapper of the entire dropdown group */
  FILTER_DROPDOWN: 'sales-filter-dropdown',
  /** Individual dropdowns — suffixed by CommonDropdownSelector automatically */
  FILTER_DROPDOWN_OPERATOR: 'sales-filter-dropdown-operator',
  FILTER_DROPDOWN_MARKET: 'sales-filter-dropdown-market',
  FILTER_DROPDOWN_DEVICE: 'sales-filter-dropdown-device',

  // ── Table ─────────────────────────────────────────────────────────────────
  TABLE: 'sales-table',
  /** data-testid on the &lt;tr&gt; for each row, keyed by orderId */
  TABLE_ROW: (orderId: string) => `sales-table-row-${orderId}`,

  // ── Column cells (per-row, keyed by sourceOrderId) ────────────────────────
  COL_OPERATOR: (id: string) => `sales-col-operator-id-${id}`,
  COL_MARKET: (id: string) => `sales-col-market-${id}`,
  COL_DEVICE: (id: string) => `sales-col-device-${id}`,
  COL_SOURCE_ORDER_ID: (id: string) => `sales-col-source-order-id-${id}`,
  COL_ORDER_TOTAL: (id: string) => `sales-col-order-total-${id}`,
  COL_GROSS_TOTAL: (id: string) => `sales-col-gross-total-${id}`,
  COL_ORDER_DTM: (id: string) => `sales-col-order-dtm-${id}`,
  COL_CREATED_ON: (id: string) => `sales-col-created-on-${id}`,

  // ── Expanded panel ────────────────────────────────────────────────────────
  EXPANDED_PANEL: 'sales-expanded-panel',
  EXPANDED_LOADING: 'sales-expanded-loading',

  /** Meta header chips */
  EXPANDED_ORDER_ID_CHIP: 'sales-expanded-order-id-chip',
  EXPANDED_MARKET_ID_CHIP: 'sales-expanded-market-id-chip',
  EXPANDED_ORDER_DATE_CHIP: 'sales-expanded-order-date-chip',

  /** Segmented tab control (Products vs Taxes) */
  EXPANDED_TAB_CONTROL: 'sales-expanded-tab-control',

  /** Sent / Not Sent status badge in the header */
  EXPANDED_STATUS_BADGE: 'sales-expanded-status-badge',

  /** Products & Payments tab sections */
  EXPANDED_PRODUCTS_SECTION: 'sales-expanded-products-section',
  EXPANDED_PRICE_SECTION: 'sales-expanded-price-section',
  EXPANDED_PAYMENTS_SECTION: 'sales-expanded-payments-section',

  /** Receipt summary line rows */
  EXPANDED_RECEIPT_PRODUCTS_ROW: 'sales-expanded-receipt-products-row',
  EXPANDED_RECEIPT_FEE_ROW: 'sales-expanded-receipt-fee-row',
  EXPANDED_RECEIPT_TAX_ROW: 'sales-expanded-receipt-tax-row',
  EXPANDED_RECEIPT_GROSS_TOTAL: 'sales-expanded-receipt-gross-total',
  EXPANDED_RECEIPT_NET_TOTAL: 'sales-expanded-receipt-net-total',

  /** Taxes & Fees tab sections */
  EXPANDED_TAX_SECTION: 'sales-expanded-tax-section',
  EXPANDED_SALES_TAX_SECTION: 'sales-expanded-sales-tax-section',
  EXPANDED_FEES_SECTION: 'sales-expanded-fees-section',
  EXPANDED_SALE_FEES_SECTION: 'sales-expanded-sale-fees-section',
} as const;
